import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CartService } from '@service/cart/cart.service';

@Component({
  selector: 'app-promo-banner',
  templateUrl: './promo-banner.component.html',
  styleUrls: ['./promo-banner.component.scss'],
  standalone: false,
})
export class PromoBannerComponent implements OnInit, OnDestroy
{
  @Input() procent: string; // np. "25"
  @Input() tekst1: string; // np. "przy zakupie"
  @Input() tekst2: string; // np. "za 500 zł*"
  @Input() kod: string; // np. "EKSTRA25"
  @Input() dataKonca: Date | string; // Data końca promocji

  public days: number = 0;
  public hours: number = 0;
  public minutes: number = 0;
  public seconds: number = 0;
  public isExpired: boolean = false;
  public isCopied: boolean = false;

  private destroy$: Subject<void> = new Subject();
  private endDate: Date;
  private copyTimeout: any = null;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private cartService: CartService
  ) { }

  public ngOnInit(): void
  {
    // Konwersja daty końca na obiekt Date
    if (typeof this.dataKonca === 'string')
    {
      this.endDate = new Date(this.dataKonca);
    } else
    {
      this.endDate = this.dataKonca;
    }

    // Sprawdzenie czy data jest poprawna
    if (isNaN(this.endDate.getTime()))
    {
      this.isExpired = true;
      return;
    }

    // Inicjalizacja odliczania
    this.updateCountdown();

    // Aktualizacja co sekundę
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() =>
      {
        this.updateCountdown();
        this.changeDetector.detectChanges();
      });
  }

  private updateCountdown(): void
  {
    const now = new Date();
    const diff = this.endDate.getTime() - now.getTime();

    if (diff <= 0)
    {
      this.isExpired = true;
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
      return;
    }

    this.days = Math.floor(diff / (1000 * 60 * 60 * 24));
    this.hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    this.minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    this.seconds = Math.floor((diff % (1000 * 60)) / 1000);
  }

  public copyToClipboard(): void
  {
    if (!this.kod)
    {
      return;
    }

    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = this.kod;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();

    try
    {
      document.execCommand('copy');
    } catch (err)
    {
      // Fallback dla nowoczesnych przeglądarek
      navigator.clipboard?.writeText(this.kod);
    }

    document.body.removeChild(selBox);

    // Wyślij informację do backendu o zastosowaniu kodu rabatowego
    this.cartService.addCoupon(this.kod)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () =>
        {
          // Kod został pomyślnie zastosowany
        },
        error: (err) =>
        {
          // Obsługa błędu - kod może być już zastosowany lub nieprawidłowy
          console.error('Błąd podczas dodawania kuponu:', err);
        }
      });

    // Pokaż napis "SKOPIOWANO" na 2.5 sekundy
    this.isCopied = true;
    this.changeDetector.detectChanges();

    // Anuluj poprzedni timeout jeśli istnieje
    if (this.copyTimeout)
    {
      clearTimeout(this.copyTimeout);
    }

    // Ustaw timeout aby wrócić do oryginalnego kodu
    this.copyTimeout = setTimeout(() =>
    {
      this.isCopied = false;
      this.changeDetector.detectChanges();
      this.copyTimeout = null;
    }, 2500);
  }

  public formatTime(value: number): string
  {
    return value.toString().padStart(2, '0');
  }

  public ngOnDestroy(): void
  {
    // Anuluj timeout jeśli istnieje
    if (this.copyTimeout)
    {
      clearTimeout(this.copyTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}

